import { useState, useRef, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight, Calendar, ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "Alle" },
  { id: "software", label: "Software" },
  { id: "projekt", label: "Projekte" },
];

const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

const typeBadge = {
  software: { label: "Software", bg: "bg-lab-primary", text: "text-white" },
  projekt: { label: "Projekt", bg: "bg-lab-accent", text: "text-lab-primary" },
};

const ProjectDetailModal = ({ project, onClose }) => {
  const [activeImg, setActiveImg] = useState(0);
  if (!project) return null;
  const badge = typeBadge[project.type] || typeBadge.projekt;
  const gallery = project.images?.length > 0 ? project.images : [project.image];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
      onClick={onClose}
      data-testid="project-detail-modal"
    >
      <div className="absolute inset-0 bg-lab-primary/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.3 }}
        className="relative bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-lab-accent transition-colors"
          data-testid="modal-close-btn"
        >
          <X className="w-5 h-5 text-lab-primary" />
        </button>

        {/* Main Image */}
        <div className="relative aspect-[16/9] overflow-hidden bg-lab-bg-light">
          <AnimatePresence mode="wait">
            <motion.img
              key={activeImg}
              src={resolveImg(gallery[activeImg])}
              alt={`${project.title} – Bild ${activeImg + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>

          {/* Navigation Arrows */}
          {gallery.length > 1 && (
            <>
              <button
                onClick={() => setActiveImg((prev) => (prev - 1 + gallery.length) % gallery.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-5 h-5 text-lab-primary" />
              </button>
              <button
                onClick={() => setActiveImg((prev) => (prev + 1) % gallery.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-5 h-5 text-lab-primary" />
              </button>
              {/* Counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm px-3 py-1 text-xs text-lab-primary font-medium">
                {activeImg + 1} / {gallery.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {gallery.length > 1 && (
          <div className="flex gap-2 px-8 pt-4" data-testid="gallery-thumbnails">
            {gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`w-16 h-12 overflow-hidden flex-shrink-0 border-2 transition-colors ${
                  i === activeImg ? "border-lab-accent" : "border-transparent hover:border-lab-border"
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={resolveImg(img)} alt={`Thumbnail ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className={`${badge.bg} ${badge.text} px-3 py-1 text-xs font-semibold tracking-wide`}>{badge.label}</span>
            <span className="text-lab-text-muted text-sm flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {project.year}
            </span>
          </div>

          <h3 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">{project.title}</h3>
          <p className="text-lab-text-muted leading-relaxed mb-6">{project.description}</p>

          <div className="mb-6">
            <h4 className="text-sm font-semibold text-lab-primary mb-3 uppercase tracking-wider">Technologien</h4>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, i) => (
                <span key={i} className="text-sm bg-lab-bg-light text-lab-text px-3 py-1.5 border border-lab-border">{tech}</span>
              ))}
            </div>
          </div>

          {project.link && (
            <a
              href={project.link}
              target={project.link.startsWith("http") ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-lab-primary text-white px-6 py-3 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors"
              data-testid="modal-project-link"
            >
              Projekt ansehen <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ProjectCard = ({ project, index, onSelect }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const badge = typeBadge[project.type] || typeBadge.projekt;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white border border-lab-border overflow-hidden card-hover group cursor-pointer"
      data-testid={`project-card-${project.id}`}
      onClick={() => onSelect(project)}
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-lab-bg-light">
        <img
          src={resolveImg(project.image)}
          alt={project.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Featured badge */}
        <div className="absolute top-4 left-4">
          <span className={`${badge.bg} ${badge.text} px-3 py-1 text-xs font-semibold tracking-wide`}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="font-playfair text-xl font-semibold text-lab-primary group-hover:text-lab-accent transition-colors duration-200">
            {project.title}
          </h3>
        </div>

        <p className="text-lab-text-muted text-sm leading-relaxed mb-4 line-clamp-2">
          {project.description}
        </p>

        {/* Technologies */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.technologies.slice(0, 3).map((tech, i) => (
            <span
              key={i}
              className="text-xs bg-lab-bg-light text-lab-text-muted px-2 py-1"
            >
              {tech}
            </span>
          ))}
          {project.technologies.length > 3 && (
            <span className="text-xs text-lab-text-muted px-2 py-1">
              +{project.technologies.length - 3}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-lab-border">
          <div className="flex items-center gap-2 text-lab-text-muted text-sm">
            <Calendar className="w-4 h-4" />
            <span>{project.year}</span>
          </div>
          
          <span
            className="text-lab-accent font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all duration-200"
            data-testid={`project-details-${project.id}`}
          >
            Details ansehen
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export const SoftwareShowcaseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API}/portfolio`).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const filteredProjects =
    activeCategory === "all"
      ? projects
      : projects.filter((p) => p.type === activeCategory);

  return (
    <section
      id="software"
      className="section-padding bg-white"
      data-testid="software-showcase-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
        >
          <div>
            <span className="label-text">Portfolio</span>
            <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4">
              Entwickelte Software & Projekte
            </h2>
            <p className="text-lab-text-muted mt-3 max-w-xl">
              Massgeschneiderte Lösungen, die wir für unsere Kunden entwickelt haben.
            </p>
          </div>
        </motion.div>

        {/* Projects Grid — max 3 on homepage */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.slice(0, 3).map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} onSelect={setSelectedProject} />
          ))}
        </div>

        {/* Link to full portfolio */}
        <div className="mt-10 text-center">
          <a href="/portfolio" className="inline-flex items-center gap-2 text-lab-accent hover:text-lab-primary font-medium text-sm transition-colors" data-testid="portfolio-view-all">
            Alle Projekte ansehen <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lab-text-muted">
              Keine Projekte in dieser Kategorie gefunden.
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default SoftwareShowcaseSection;
